/**
 * Client-side port of packages/ops/scripts/audit-vera-team-sync.ts for the
 * /admin Access Health tab.
 *
 * Finds users whose in-app Vera roles and Dataverse team memberships have
 * drifted: a role granted in vba_userroleassignment (→ UI access) with no
 * membership in the mapped Dataverse team (→ data access). Such users see
 * the module pages but every save silently fails with a missing-privilege
 * error. Repairs by adding the missing membership, the same call the /admin
 * role toggles use.
 *
 * Runs with the signed-in admin's delegated token against the currently
 * selected environment. Requires the team/user privileges granted to the
 * Vera Admin role by provision-vera-security-roles.ts (Read/Write/Append/
 * AppendTo on team; Read/Append/AppendTo on systemuser).
 */

import { dataverseClient, TABLES } from '@shared/api';
import type { TableName } from '@shared/api/dataverse/types';
import { teamForRole, type VeraTeamName } from '@shared/auth';

const TEAMS_SET = 'teams' as TableName;
const SYSTEMUSERS_SET = 'systemusers' as TableName;
const NAV = 'teammembership_association';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface TeamSyncDrift {
  /** Bare AAD object id (no tenant suffix). */
  oid: string;
  upn: string;
  fullname: string;
  /** Null when the user has never signed in (no systemuser record yet). */
  systemuserid: string | null;
  /** Disabled accounts can't be added to teams, and can't sign in, so the gap is harmless. */
  disabled: boolean;
  roles: string[];
  missingTeams: VeraTeamName[];
}

/** Per-user status for every checked user (healthy or not) — feeds the
 *  Access Health "check one user" search. */
export interface TeamSyncUserStatus {
  oid: string;
  upn: string;
  fullname: string;
  roles: string[];
  expectedTeams: VeraTeamName[];
  missingTeams: VeraTeamName[];
  disabled: boolean;
  noSystemUser: boolean;
}

export interface TeamSyncAuditResult {
  checkedUsers: number;
  drift: TeamSyncDrift[];
  /** Every checked user, including healthy ones. */
  users: TeamSyncUserStatus[];
  /** Teams expected by teamForRole() but absent in Dataverse; run provision-vera-teams. */
  teamsNotProvisioned: string[];
}

interface GrantRow {
  vba_userobjectid?: string;
  vba_userprincipalname?: string;
  vba_rolevalue?: string;
}
interface TeamRow {
  teamid: string;
  name?: string;
}
interface UserRow {
  systemuserid: string;
  fullname?: string;
  isdisabled?: boolean;
  azureactivedirectoryobjectid?: string;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export async function auditVeraTeamSync(): Promise<TeamSyncAuditResult> {
  // 1. Every in-app grant, grouped by bare OID. Pre-provisioned (email-keyed)
  //    rows have no OID yet; the BFF reconciles those at first sign-in; skip.
  const grants = await dataverseClient.listAll<GrantRow>(TABLES.USER_ROLE_ASSIGNMENT, {
    select: ['vba_userobjectid', 'vba_userprincipalname', 'vba_rolevalue'],
  });
  const byUser = new Map<string, { upn: string; roles: Set<string> }>();
  for (const g of grants) {
    const bareOid = (g.vba_userobjectid ?? '').split('.')[0] ?? '';
    if (!GUID_RE.test(bareOid)) continue;
    const entry = byUser.get(bareOid) ?? { upn: g.vba_userprincipalname ?? '', roles: new Set<string>() };
    if (g.vba_rolevalue) entry.roles.add(g.vba_rolevalue);
    byUser.set(bareOid, entry);
  }

  // 2. Vera teams + full member list per team (one request per team, rather
  //    than one membership lookup per user).
  const teams = await dataverseClient.listAll<TeamRow>(TEAMS_SET, {
    filter: "startswith(name,'Vera ')",
    select: ['teamid', 'name'],
  });
  const teamIdByName = new Map(teams.map((t) => [t.name ?? '', t.teamid]));
  const membersByTeam = new Map<string, Set<string>>();
  await Promise.all(
    [...teamIdByName.entries()].map(async ([name, id]) => {
      const members = await dataverseClient.listAll<UserRow>(`teams(${id})/${NAV}`, {
        select: ['systemuserid'],
      });
      membersByTeam.set(name, new Set(members.map((m) => m.systemuserid)));
    }),
  );

  // 3. Resolve systemusers for every granted OID (chunked OR-filters).
  const userByOid = new Map<string, UserRow>();
  for (const part of chunk([...byUser.keys()], 40)) {
    const filter = part.map((oid) => `azureactivedirectoryobjectid eq ${oid}`).join(' or ');
    const rows = await dataverseClient.listAll<UserRow>(SYSTEMUSERS_SET, {
      filter,
      select: ['systemuserid', 'fullname', 'isdisabled', 'azureactivedirectoryobjectid'],
    });
    for (const r of rows) {
      if (r.azureactivedirectoryobjectid) userByOid.set(r.azureactivedirectoryobjectid.toLowerCase(), r);
    }
  }

  // 4. Diff expected teams (from roles) against actual memberships.
  const drift: TeamSyncDrift[] = [];
  const users: TeamSyncUserStatus[] = [];
  const teamsNotProvisioned = new Set<string>();
  for (const [oid, { upn, roles }] of byUser) {
    const expected = new Set<VeraTeamName>();
    for (const r of roles) {
      const t = teamForRole(r);
      if (t) expected.add(t);
    }
    if (expected.size === 0) continue;

    const su = userByOid.get(oid.toLowerCase());
    const missing: VeraTeamName[] = [];
    for (const t of expected) {
      if (!teamIdByName.has(t)) {
        teamsNotProvisioned.add(t);
        continue;
      }
      if (!su || !membersByTeam.get(t)?.has(su.systemuserid)) missing.push(t);
    }
    users.push({
      oid,
      upn,
      fullname: su?.fullname ?? upn,
      roles: [...roles].sort(),
      expectedTeams: [...expected],
      missingTeams: missing,
      disabled: su?.isdisabled ?? false,
      noSystemUser: !su,
    });
    if (missing.length > 0) {
      drift.push({
        oid,
        upn,
        fullname: su?.fullname ?? upn,
        systemuserid: su?.systemuserid ?? null,
        disabled: su?.isdisabled ?? false,
        roles: [...roles].sort(),
        missingTeams: missing,
      });
    }
  }

  return { checkedUsers: byUser.size, drift, users, teamsNotProvisioned: [...teamsNotProvisioned] };
}

/** Add one user to one Vera team: the repair for a drift row. */
export async function addUserToVeraTeam(teamName: VeraTeamName, systemuserid: string): Promise<void> {
  const teams = await dataverseClient.list<TeamRow>(TEAMS_SET, {
    filter: `name eq '${teamName.replace(/'/g, "''")}'`,
    select: ['teamid'],
    top: 1,
  });
  const team = teams.value[0];
  if (!team) throw new Error(`Team "${teamName}" not found in Dataverse; run provision-vera-teams.`);
  await dataverseClient.associate(TEAMS_SET, team.teamid, NAV, SYSTEMUSERS_SET, systemuserid);
}
